import { Settings, Save, Lock, ExternalLink, Building2, Image, Loader2, Upload, Languages, UserCheck, MessageSquare, Bell, Plus, Trash2, GraduationCap, RefreshCcw, CheckCircle2, Bot, Radio, Volume2, CreditCard } from 'lucide-react';
import DepartmentTreeSelect from '../e-education/components/selectors/DepartmentTreeSelect';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import TabPage, { TabItemProps } from '../../components/dynamic/Tab';
import checkPermission from '../../utils/permission';
import { Select, SelectItem } from '@heroui/select';
import { ApiService } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../../hooks/useModal';
import DepartmentSelector from '../../components/selectors/DepartmentSelector';
import { isYesToolsHostname } from '../../utils/yesTools';
import { defaultMenuItems } from '../../sidebar/data/defaultMenuItems';
import { getPaymentProviderMeta } from '../../utils/paymentProviders';

const SUPPORTED_LANGUAGES = [
  { code: 'tr-TR', name: 'Türkçe', countryCode: 'tr' },
  { code: 'en-US', name: 'English', countryCode: 'us' },
];

const getFlagUrl = (countryCode: string) => `https://flagcdn.com/w40/${countryCode}.png`;

const faviconFiles = [
  'apple-touch-icon.png',
  'favicon-96x96.png',
  'favicon.ico',
  'favicon.svg',
  'web-app-manifest-192x192.png',
  'web-app-manifest-512x512.png'
];

interface AiTeacherAudioModel {
  id: string;
  label: string;
  mode: 'tts' | 'live' | 'browser' | 'none';
  description?: string;
}

const AI_TEACHER_AUDIO_MODELS: AiTeacherAudioModel[] = [
  {
    id: 'gemini-2.5-flash-preview-tts',
    label: 'Gemini 2.5 Flash TTS (ekonomik)',
    mode: 'tts',
    description: 'Metin yanıtını sese çevirir; 3.1 TTS ile aynı iş, yaklaşık yarı maliyet.',
  },
  {
    id: 'gemini-3.1-flash-tts-preview',
    label: 'Gemini 3.1 Flash TTS',
    mode: 'tts',
    description: 'Metin yanıtını yüksek kaliteli ses dosyasına çevirir.',
  },
  {
    id: 'gemini-3.1-flash-live-preview',
    label: 'Gemini 3.1 Flash Live Preview',
    mode: 'live',
    description: 'Cevap ve sesi aynı düşük gecikmeli Live API oturumunda üretir.',
  },
  {
    id: 'browser-tts',
    label: 'Tarayıcı Sesi (ücretsiz)',
    mode: 'browser',
    description: 'Seslendirme öğrencinin cihazında yapılır; API maliyeti sıfırdır. Ses kalitesi cihaza göre değişir.',
  },
  {
    id: 'text-only',
    label: 'Sessiz (sadece yazılı sohbet)',
    mode: 'none',
    description: 'AI öğretmen hiç seslendirme yapmaz; öğrenci yalnızca yazışır. Ses maliyeti oluşmaz.',
  },
];

/** NewUser.tsx ile uyumlu form kabuğu; her sekmede accent farklı */
type SettingsFormAccent =
  | 'emerald'
  | 'violet'
  | 'cyan'
  | 'amber'
  | 'orange'
  | 'indigo'
  | 'rose'
  | 'sky'
  | 'fuchsia';

const SETTINGS_FORM_PAGE = 'h-full overflow-y-auto bg-gray-100 p-2 sm:p-2';
const SETTINGS_PAGE_INNER = 'pb-10 max-w-6xl mx-auto';
const SETTINGS_SHELL = 'bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6';
const SETTINGS_SECTION = 'rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm';
const SETTINGS_PAGE_TITLE_CLASS = 'text-[19px] sm:text-[23px] font-semibold text-gray-900 flex items-center gap-2';

const SETTINGS_INPUT_BASE =
  'w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400';

const settingsAccent: Record<
  SettingsFormAccent,
  {
    badge: string;
    underline: string;
    field: string;
    primaryBtn: string;
  }
> = {
  emerald: {
    badge: 'inline-flex items-center rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700',
    underline: 'bg-emerald-200',
    field:
      'hover:border-slate-300 hover:bg-white focus:border-emerald-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-emerald-50 disabled:bg-slate-100 disabled:text-slate-500',
    primaryBtn:
      'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700 shadow-sm',
  },
  violet: {
    badge: 'inline-flex items-center rounded-full border border-violet-100 bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700',
    underline: 'bg-violet-200',
    field:
      'hover:border-slate-300 hover:bg-white focus:border-violet-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-violet-50 disabled:bg-slate-100 disabled:text-slate-500',
    primaryBtn:
      'bg-gradient-to-r from-violet-600 to-purple-600 text-white hover:from-violet-700 hover:to-purple-700 shadow-sm',
  },
  cyan: {
    badge: 'inline-flex items-center rounded-full border border-cyan-100 bg-cyan-50 px-2.5 py-1 text-xs font-semibold text-cyan-700',
    underline: 'bg-cyan-200',
    field:
      'hover:border-slate-300 hover:bg-white focus:border-cyan-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-cyan-50 disabled:bg-slate-100 disabled:text-slate-500',
    primaryBtn:
      'bg-gradient-to-r from-cyan-600 to-teal-600 text-white hover:from-cyan-700 hover:to-teal-700 shadow-sm',
  },
  amber: {
    badge: 'inline-flex items-center rounded-full border border-amber-100 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800',
    underline: 'bg-amber-200',
    field:
      'hover:border-slate-300 hover:bg-white focus:border-amber-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-amber-50 disabled:bg-slate-100 disabled:text-slate-500',
    primaryBtn:
      'bg-gradient-to-r from-amber-600 to-orange-600 text-white hover:from-amber-700 hover:to-orange-700 shadow-sm',
  },
  orange: {
    badge: 'inline-flex items-center rounded-full border border-orange-100 bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-800',
    underline: 'bg-orange-200',
    field:
      'hover:border-slate-300 hover:bg-white focus:border-orange-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-orange-50 disabled:bg-slate-100 disabled:text-slate-500',
    primaryBtn:
      'bg-gradient-to-r from-orange-600 to-amber-600 text-white hover:from-orange-700 hover:to-amber-700 shadow-sm',
  },
  indigo: {
    badge: 'inline-flex items-center rounded-full border border-indigo-100 bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700',
    underline: 'bg-indigo-200',
    field:
      'hover:border-slate-300 hover:bg-white focus:border-indigo-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-indigo-50 disabled:bg-slate-100 disabled:text-slate-500',
    primaryBtn:
      'bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:from-indigo-700 hover:to-violet-700 shadow-sm',
  },
  rose: {
    badge: 'inline-flex items-center rounded-full border border-rose-100 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700',
    underline: 'bg-rose-200',
    field:
      'hover:border-slate-300 hover:bg-white focus:border-rose-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-rose-50 disabled:bg-slate-100 disabled:text-slate-500',
    primaryBtn:
      'bg-gradient-to-r from-rose-600 to-pink-600 text-white hover:from-rose-700 hover:to-pink-700 shadow-sm',
  },
  sky: {
    badge: 'inline-flex items-center rounded-full border border-sky-100 bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-800',
    underline: 'bg-sky-200',
    field:
      'hover:border-slate-300 hover:bg-white focus:border-sky-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-sky-50 disabled:bg-slate-100 disabled:text-slate-500',
    primaryBtn:
      'bg-gradient-to-r from-sky-600 to-blue-600 text-white hover:from-sky-700 hover:to-blue-700 shadow-sm',
  },
  fuchsia: {
    badge: 'inline-flex items-center rounded-full border border-fuchsia-100 bg-fuchsia-50 px-2.5 py-1 text-xs font-semibold text-fuchsia-700',
    underline: 'bg-fuchsia-200',
    field:
      'hover:border-slate-300 hover:bg-white focus:border-fuchsia-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-fuchsia-50 disabled:bg-slate-100 disabled:text-slate-500',
    primaryBtn:
      'bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white hover:from-fuchsia-700 hover:to-pink-700 shadow-sm',
  },
};

function settingsInputClass(accent: SettingsFormAccent) {
  return `${SETTINGS_INPUT_BASE} ${settingsAccent[accent].field}`;
}

interface SettingsData {
  id: string;
  companyName: string;
  companyLogoId: string | null;
  defaultLanguage?: string;
  media?: {
    id: string;
    url: string;
    name: string;
    type: string;
  } | null;

  faviconAppleTouchIcon?: string | null;
  favicon96x96?: string | null;
  faviconIco?: string | null;
  faviconSvg?: string | null;
  siteWebmanifest?: string | null;
  faviconManifest192?: string | null;
  faviconManifest512?: string | null;
  n8nChatWebhookUrl?: string | null;
  n8nLatexExtractorWebhookUrl?: string | null;
  announcementCount?: number;
}

interface ExamDateItem {
  id: string;
  name: string;
  date: string;
  departmentIds: string[];
  allDepartments: boolean;
}

interface ExamDepartmentNode {
  id: string;
  name: string;
  parentId?: string | null;
}

const FAVICON_FIELD_MAP: Record<string, keyof SettingsData> = {
  'apple-touch-icon.png': 'faviconAppleTouchIcon',
  'favicon-96x96.png': 'favicon96x96',
  'favicon.ico': 'faviconIco',
  'favicon.svg': 'faviconSvg',
  'site.webmanifest': 'siteWebmanifest',
  'web-app-manifest-192x192.png': 'faviconManifest192',
  'web-app-manifest-512x512.png': 'faviconManifest512',
};

const useSystemSettings = () => {
  const [settingsData, setSettingsData] = useState<SettingsData | null>(null);
  const [fetching, setFetching] = useState(true);
  const { showToast } = useModal();

  const fetchSettings = useCallback(async () => {
    try {
      setFetching(true);
      const response = await ApiService.getApi('settings');
      if (response) {
        setSettingsData(response);
      }
    } catch (error) {
      console.error('Ayarlar yüklenirken hata:', error);
      showToast('Ayarlar yüklenirken bir hata oluştu', 'error');
    } finally {
      setFetching(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return { settingsData, setSettingsData, fetching, fetchSettings };
};

const LoadingState = () => (
  <div className='h-full flex items-center justify-center p-6'>
    <div className='flex flex-col items-center gap-3'>
      <Loader2 className='w-8 h-8 animate-spin text-blue-600' />
      <p className='text-gray-600 text-sm'>Ayarlar yükleniyor...</p>
    </div>
  </div>
);

function GeneralSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();
  const { settingsData, fetching, setSettingsData } = useSystemSettings();

  const [companyName, setCompanyName] = useState('');
  const [defaultLanguage, setDefaultLanguage] = useState('tr-TR');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (settingsData) {
      setCompanyName(settingsData.companyName || '');
      setDefaultLanguage(settingsData.defaultLanguage || 'tr-TR');
      if (settingsData.media?.url) setLogoPreview(settingsData.media.url);
    }
  }, [settingsData]);

  const handleLogoChange = (file: File | null) => {
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasUpdatePermission) return;
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('companyName', companyName);
      formData.append('defaultLanguage', defaultLanguage);

      if (logoFile) formData.append('companyLogo', logoFile);

      const response = await ApiService.updateSettings(formData);

      if (!response) throw new Error('Kayıt başarısız');

      setSettingsData(response);
      if (response.media?.url) setLogoPreview(response.media.url);

      setLogoFile(null);
      showToast('Sistem ayarları başarıyla güncellendi', 'success');
    } catch (error: any) {
      console.error('Ayarlar güncellenirken hata:', error);
      showToast(error?.message || 'Ayarlar güncellenirken bir hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <LoadingState />;

  const accent: SettingsFormAccent = 'emerald';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5'>
            <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
              <Settings className='w-6 h-6 text-slate-600' />
              Genel Bilgiler
            </h1>
            <p className='text-sm text-gray-600 mt-1'>Şirket adı ve logo gibi temel ayarları buradan yönetebilirsiniz.</p>
            <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
          </div>

          <form onSubmit={handleSubmit} className='space-y-5'>
            <div className={SETTINGS_SECTION}>
              <div className='mb-3'>
                <span className={a.badge}>Kurum bilgileri</span>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                    <Building2 className='w-4 h-4 text-emerald-600' />
                    Şirket / Kurum Adı
                  </label>
                  <input
                    type='text'
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    disabled={!hasUpdatePermission}
                    className={settingsInputClass(accent)}
                    placeholder='Sistem Adını Giriniz'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                    <Image className='w-4 h-4 text-emerald-600' />
                    Sistem Logosu
                  </label>
                  <div className='flex items-center gap-3'>
                    {logoPreview && (
                      <div className='w-12 h-12 flex-shrink-0 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-center overflow-hidden shadow-sm'>
                        <img
                          src={logoPreview}
                          alt='Logo'
                          className='w-full h-full object-contain p-1'
                        />
                      </div>
                    )}

                    <label className={`flex-1 flex items-center justify-center px-3.5 py-2.5 border border-dashed rounded-xl border-emerald-200 bg-gradient-to-b from-emerald-50/80 to-white shadow-sm transition-colors ${hasUpdatePermission ? 'cursor-pointer hover:border-emerald-300 hover:bg-white' : 'cursor-not-allowed opacity-60'}`}>
                      <Upload className='w-4 h-4 mr-2 text-slate-500' />
                      <span className='text-sm text-gray-600 truncate'>
                        {logoFile ? logoFile.name : 'Logo Yükle'}
                      </span>
                      <input
                        type='file'
                        accept='image/*'
                        className='hidden'
                        onChange={(e) => handleLogoChange(e.target.files?.[0] || null)}
                        disabled={!hasUpdatePermission}
                      />
                    </label>

                    {logoPreview && hasUpdatePermission && (
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm('Sistem logosunu sıfırlamak istediğinize emin misiniz?')) return;
                          setLoading(true);
                          try {
                            await ApiService.resetCompanyLogo();
                            setLogoPreview(null);
                            setLogoFile(null);
                            showToast('Logo başarıyla sıfırlandı', 'success');
                          } catch (error) {
                            showToast('Logo sıfırlanırken hata oluştu', 'error');
                          } finally {
                            setLoading(false);
                          }
                        }}
                        className="p-2.5 text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors"
                        title="Logoyu Sıfırla"
                      >
                        <i className="fa fa-trash" />
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-trash-2"><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /><line x1="10" x2="10" y1="11" y2="17" /><line x1="14" x2="14" y1="11" y2="17" /></svg>
                      </button>
                    )}
                  </div>
                </div>

                <div className='md:col-span-2'>
                  <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                    <Languages className='w-4 h-4 text-emerald-600' />
                    Varsayılan Dil
                  </label>
                  <Select
                    aria-label="Varsayılan Dil"
                    selectedKeys={[defaultLanguage]}
                    onSelectionChange={(keys) => {
                      const selected = Array.from(keys)[0];
                      if (selected) setDefaultLanguage(selected.toString());
                    }}
                    isDisabled={!hasUpdatePermission}
                    classNames={{
                      trigger: "bg-slate-50 border border-slate-200/90 hover:border-emerald-300 rounded-xl h-11 min-h-11 shadow-sm data-[hover=true]:bg-white",
                      value: "text-slate-800 font-medium text-sm",
                      popoverContent: "bg-white rounded-xl shadow-xl border border-slate-200",
                    }}
                    renderValue={(items) => {
                      const lang = SUPPORTED_LANGUAGES.find(l => l.code === defaultLanguage);
                      return lang ? (
                        <div className="flex items-center gap-3">
                          <img
                            src={getFlagUrl(lang.countryCode)}
                            alt={lang.name}
                            className="w-6 h-4 object-cover rounded-sm shadow-sm"
                          />
                          <span>{lang.name}</span>
                        </div>
                      ) : null;
                    }}
                  >
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <SelectItem
                        key={lang.code}
                        textValue={lang.name}
                        startContent={
                          <img
                            src={getFlagUrl(lang.countryCode)}
                            alt={lang.name}
                            className="w-7 h-5 object-cover rounded-sm shadow-sm"
                          />
                        }
                      >
                        <span className="font-medium">{lang.name}</span>
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </div>
            </div>

            <div className='sticky bottom-0 z-10 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0'>
              <div className='flex justify-end'>
                <button
                  type='submit'
                  disabled={!hasUpdatePermission || loading}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 transition-all ${a.primaryBtn}`}
                >
                  {loading ? (
                    <Loader2 className='w-4 h-4 animate-spin' />
                  ) : (
                    <Save className='w-4 h-4' />
                  )}
                  {loading ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function FaviconSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();
  const { settingsData, fetching } = useSystemSettings();

  const [faviconFiles_, setFaviconFiles] = useState<Record<string, File>>({});
  const [faviconLoading, setFaviconLoading] = useState(false);

  const handleFaviconChange = (fileName: string, file: File | null) => {
    if (file) setFaviconFiles(prev => ({ ...prev, [fileName]: file }));
  };

  const handleFaviconUpload = async () => {
    if (Object.keys(faviconFiles_).length === 0) {
      showToast('Lütfen en az bir favicon dosyası seçin', 'warning');
      return;
    }

    setFaviconLoading(true);
    try {
      const formData = new FormData();
      for (const [name, file] of Object.entries(faviconFiles_)) {
        formData.append(name, file);
      }

      await ApiService.uploadFavicons(formData);

      setFaviconFiles({});
      showToast('Favicon dosyaları başarıyla yüklendi', 'success');

      window.location.reload();
    } catch (error: any) {
      console.error('Favicon yüklenirken hata:', error);

      if (error?.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map((err: any) =>
          err.error || `${err.file}: Boyut hatalı`
        ).join('\n');

        showToast(`Boyut uyumsuzluğu: ${errorMessages}`, 'error');
      } else {
        showToast(error?.message || 'Favicon yüklenirken bir hata oluştu', 'error');
      }
    } finally {
      setFaviconLoading(false);
    }
  };

  if (fetching) return <LoadingState />;

  const accent: SettingsFormAccent = 'violet';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
            <div>
              <h1 className='text-xl sm:text-2xl font-semibold text-gray-900 flex items-center gap-2'>
                <Image className='w-6 h-6 text-violet-600' />
                Favicon Dosyaları
              </h1>
              <p className='text-sm text-gray-600 mt-1'>Sitenizin tarayıcı sekmelerinde ve mobil cihazlarda nasıl görüneceğini yönetin.</p>
              <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
            </div>
            <a
              href='https://realfavicongenerator.net'
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-violet-700 bg-violet-50 px-3 py-2 rounded-xl border border-violet-200 hover:bg-violet-100 transition-colors shadow-sm'
            >
              <ExternalLink className='w-3.5 h-3.5' />
              Favicon Oluşturucu
            </a>
          </div>

          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Dosya yükleme</span>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
              {faviconFiles.map((fileName) => {
                if (fileName === 'site.webmanifest') return null;
                const faviconField = FAVICON_FIELD_MAP[fileName];
                const faviconUrl = settingsData && faviconField ? (settingsData[faviconField] as string | null) : null;

                return (
                  <div key={fileName}>
                    <label className='block text-sm font-medium text-gray-700 mb-2 truncate' title={fileName}>
                      {fileName}
                    </label>

                    <div className='flex gap-4 items-start'>
                      {(fileName.endsWith('.png') || fileName.endsWith('.ico') || fileName.endsWith('.svg')) && (
                        <div className='w-12 h-12 flex-shrink-0 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-center overflow-hidden shadow-sm'>
                          {faviconUrl ? (
                            <img
                              src={faviconUrl}
                              alt={fileName}
                              className='w-full h-full object-contain p-1 opacity-90'
                              onError={(e) => e.currentTarget.style.display = 'none'}
                            />
                          ) : (
                            <span className='text-xs text-slate-400'>Yok</span>
                          )}
                        </div>
                      )}

                      <label className={`flex-1 flex items-center justify-center px-3.5 py-3 border border-dashed rounded-xl bg-gradient-to-b from-violet-50/80 to-white border-violet-200 shadow-sm transition-colors min-h-[3rem] ${hasUpdatePermission ? 'cursor-pointer hover:border-violet-300 hover:bg-white' : 'cursor-not-allowed opacity-60'}`}>
                        <span className='text-xs text-gray-600 truncate font-medium'>
                          {faviconFiles_[fileName] ? faviconFiles_[fileName].name : 'Dosya Seç'}
                        </span>
                        <input
                          type='file'
                          className='hidden'
                          onChange={(e) => handleFaviconChange(fileName, e.target.files?.[0] || null)}
                          disabled={!hasUpdatePermission}
                        />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className='flex flex-wrap items-center justify-end gap-3 mt-6 pt-4 border-t border-slate-100'>
              <button
                type='button'
                onClick={async () => {
                  if (!window.confirm('Tüm favicon dosyalarını sıfırlamak istediğinize emin misiniz?')) return;
                  setFaviconLoading(true);
                  try {
                    await ApiService.resetFavicons();
                    showToast('Favicon ayarları sıfırlandı', 'success');
                    window.location.reload();
                  } catch (error) {
                    showToast('Favicon sıfırlanırken hata oluştu', 'error');
                  } finally {
                    setFaviconLoading(false);
                  }
                }}
                disabled={!hasUpdatePermission || faviconLoading}
                className='inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium'
              >
                Sıfırla
              </button>

              <button
                type='button'
                onClick={handleFaviconUpload}
                disabled={!hasUpdatePermission || faviconLoading || Object.keys(faviconFiles_).length === 0}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}
              >
                {faviconLoading ? (
                  <Loader2 className='w-4 h-4 animate-spin' />
                ) : (
                  <Upload className='w-4 h-4' />
                )}
                {faviconLoading ? 'Yükleniyor...' : 'Favicon Dosyalarını Yükle'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface Department {
  id: string;
  name: string;
  color?: string;
  parentId?: string | null;
}

interface DepartmentDropdownProps {
  value: string | null;
  onChange: (id: string | null) => void;
  onDepartmentSelect?: (department: { id: string; name: string; color?: string } | null) => void;
  selectedDepartment: { id: string; name: string; color?: string } | null;
  disabled?: boolean;
  placeholder?: string;
}

function DepartmentDropdown({ value, onChange, onDepartmentSelect, selectedDepartment, disabled, placeholder = 'Departman Seçiniz...' }: DepartmentDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const syncedSelectedDepartment =
    selectedDepartment && value && selectedDepartment.id === value
      ? selectedDepartment
      : null;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (ids: string[]) => {
    const newId = ids.length > 0 ? ids[0] : null;
    onChange(newId);
    setIsOpen(false);
  };

  const handleSelectWithDetails = (department: { id: string; name: string; color?: string } | null) => {
    onDepartmentSelect?.(department);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full rounded-xl border border-slate-200/90 bg-slate-50 px-3.5 py-2.5 text-left flex items-center justify-between shadow-sm transition-all ${disabled ? 'bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200' : 'hover:border-slate-300 hover:bg-white cursor-pointer'
          } ${isOpen ? 'ring-4 ring-slate-100 border-slate-200 bg-white' : ''}`}
      >
        <span className={syncedSelectedDepartment ? 'text-gray-900' : 'text-gray-500'}>
          {syncedSelectedDepartment ? syncedSelectedDepartment.name : placeholder}
        </span>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <DepartmentTreeSelect
            value={value ? [value] : []}
            onChange={handleSelect}
            onSelectWithDetails={handleSelectWithDetails}
            disabled={disabled}
            multiple={false}
          />
        </div>
      )}
    </div>
  );
}

function EducationSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teacherDepartmentId, setTeacherDepartmentId] = useState<string | null>(null);
  const [parentDepartmentId, setParentDepartmentId] = useState<string | null>(null);
  const [studentDepartmentId, setStudentDepartmentId] = useState<string | null>(null);
  const [teacherDepartment, setTeacherDepartment] = useState<{ id: string; name: string; color?: string } | null>(null);
  const [parentDepartment, setParentDepartment] = useState<{ id: string; name: string; color?: string } | null>(null);
  const [studentDepartment, setStudentDepartment] = useState<{ id: string; name: string; color?: string } | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const educationSettings = await ApiService.getEducationSettings();

        setTeacherDepartmentId(educationSettings.teacherDepartmentId);
        setParentDepartmentId(educationSettings.parentDepartmentId);
        setStudentDepartmentId(educationSettings.studentDepartmentId);
        setTeacherDepartment(educationSettings.teacherDepartment);
        setParentDepartment(educationSettings.parentDepartment);
        setStudentDepartment(educationSettings.studentDepartment);
      } catch (error) {
        console.error('Eğitim ayarları yüklenirken hata:', error);
        showToast('Eğitim ayarları yüklenirken bir hata oluştu', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [showToast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasUpdatePermission) return;

    setSaving(true);
    try {
      const result = await ApiService.updateEducationSettings({
        teacherDepartmentId: teacherDepartmentId || null,
        parentDepartmentId: parentDepartmentId || null,
        studentDepartmentId: studentDepartmentId || null,
      });

      const educationSettings = await ApiService.getEducationSettings();
      setTeacherDepartment(educationSettings.teacherDepartment);
      setParentDepartment(educationSettings.parentDepartment);
      setStudentDepartment(educationSettings.studentDepartment);

      showToast('Eğitim ayarları başarıyla güncellendi', 'success');
    } catch (error: any) {
      console.error('Eğitim ayarları güncellenirken hata:', error);
      showToast(error?.message || 'Eğitim ayarları güncellenirken bir hata oluştu', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;

  const accent: SettingsFormAccent = 'cyan';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5'>
            <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
              <GraduationCap className='w-6 h-6 text-cyan-600' />
              Eğitim Ayarları
            </h1>
            <p className='text-sm text-gray-600 mt-1'>Öğretmen, öğrenci ve veli departmanlarını buradan ayarlayabilirsiniz.</p>
            <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
          </div>

          <form onSubmit={handleSubmit} className='space-y-5'>
            <div className={SETTINGS_SECTION}>
              <div className='mb-3'>
                <span className={a.badge}>Departman eşlemeleri</span>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cyan-600"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                    Öğretmen Departmanı
                  </label>
                  <p className='text-xs text-gray-500 mb-2'>
                    Öğretmen raporlarında bu departman altındaki kullanıcılar listelenir.
                  </p>
                  <DepartmentDropdown
                    value={teacherDepartmentId}
                    onChange={(id) => {
                      setTeacherDepartmentId(id);
                      if (!id) setTeacherDepartment(null);
                    }}
                    onDepartmentSelect={setTeacherDepartment}
                    selectedDepartment={teacherDepartment}
                    disabled={!hasUpdatePermission}
                    placeholder="Öğretmen departmanı seçin..."
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cyan-600"><path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c3 3 9 3 12 0v-5" /></svg>
                    Öğrenci Departmanı
                  </label>
                  <p className='text-xs text-gray-500 mb-2'>
                    Öğrenci kullanıcılarının bulunduğu departmanı belirleyin.
                  </p>
                  <DepartmentDropdown
                    value={studentDepartmentId}
                    onChange={(id) => {
                      setStudentDepartmentId(id);
                      if (!id) setStudentDepartment(null);
                    }}
                    onDepartmentSelect={setStudentDepartment}
                    selectedDepartment={studentDepartment}
                    disabled={!hasUpdatePermission}
                    placeholder="Öğrenci departmanı seçin..."
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cyan-600"><path d="M9 7v-2a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /><path d="M15 11v4M9 11v4" /><rect width="20" height="14" x="2" y="7" rx="2" /></svg>
                    Veli Departmanı
                  </label>
                  <p className='text-xs text-gray-500 mb-2'>
                    Veli kullanıcılarının bulunduğu departmanı belirleyin.
                  </p>
                  <DepartmentDropdown
                    value={parentDepartmentId}
                    onChange={(id) => {
                      setParentDepartmentId(id);
                      if (!id) setParentDepartment(null);
                    }}
                    onDepartmentSelect={setParentDepartment}
                    selectedDepartment={parentDepartment}
                    disabled={!hasUpdatePermission}
                    placeholder="Veli departmanı seçin..."
                  />
                </div>
              </div>
            </div>

            <div className='sticky bottom-0 z-10 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0'>
              <div className='flex justify-end'>
                <button
                  type='submit'
                  disabled={!hasUpdatePermission || saving}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}
                >
                  {saving ? (
                    <Loader2 className='w-4 h-4 animate-spin' />
                  ) : (
                    <Save className='w-4 h-4' />
                  )}
                  {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function CafeSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cafeUnregisteredStudentDepartmentId, setCafeUnregisteredStudentDepartmentId] = useState<string | null>(null);
  const [cafeGuestUserDepartmentId, setCafeGuestUserDepartmentId] = useState<string | null>(null);
  const [cafeRegistrationApiKey, setCafeRegistrationApiKey] = useState<string>('');
  const [cafeMenuApiKey, setCafeMenuApiKey] = useState<string>('');
  const [cafeMuhasebeBranchId, setCafeMuhasebeBranchId] = useState<string | null>(null);
  const [cafeMuhasebeBranchName, setCafeMuhasebeBranchName] = useState<string>('');
  const [cafeUnregisteredStudentDepartment, setCafeUnregisteredStudentDepartment] = useState<{ id: string; name: string; color?: string } | null>(null);
  const [cafeGuestUserDepartment, setCafeGuestUserDepartment] = useState<{ id: string; name: string; color?: string } | null>(null);
  const [showRegistrationApiKey, setShowRegistrationApiKey] = useState(false);
  const [showMenuApiKey, setShowMenuApiKey] = useState(false);
  const [branchLoading, setBranchLoading] = useState(false);
  const [availableBranches, setAvailableBranches] = useState<Array<{ id: string; name: string; province?: string; district?: string }>>([]);
  const [healthChecking, setHealthChecking] = useState(false);
  const [healthStatus, setHealthStatus] = useState<{ success?: boolean; status?: string; timestamp?: string } | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const cafeSettings = await ApiService.getCafeSettings();

        setCafeUnregisteredStudentDepartmentId(cafeSettings.cafeUnregisteredStudentDepartmentId);
        setCafeGuestUserDepartmentId(cafeSettings.cafeGuestUserDepartmentId);
        setCafeRegistrationApiKey(cafeSettings.cafeRegistrationApiKey || '');
        setCafeMenuApiKey(cafeSettings.cafeMenuApiKey || '');
        setCafeMuhasebeBranchId(cafeSettings.cafeMuhasebeBranchId || null);
        setCafeMuhasebeBranchName(cafeSettings.cafeMuhasebeBranchName || '');
        setCafeUnregisteredStudentDepartment(cafeSettings.cafeUnregisteredStudentDepartment);
        setCafeGuestUserDepartment(cafeSettings.cafeGuestUserDepartment);
      } catch (error) {
        console.error('Cafe ayarları yüklenirken hata:', error);
        showToast('Cafe ayarları yüklenirken bir hata oluştu', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [showToast]);

  const generateApiKey = (setter: (value: string) => void) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = 'cafe_';
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setter(result);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasUpdatePermission) return;

    setSaving(true);
    try {
      await ApiService.updateCafeSettings({
        cafeUnregisteredStudentDepartmentId: cafeUnregisteredStudentDepartmentId || null,
        cafeGuestUserDepartmentId: cafeGuestUserDepartmentId || null,
        cafeRegistrationApiKey: cafeRegistrationApiKey || null,
        cafeMenuApiKey: cafeMenuApiKey || null,
        cafeMuhasebeBranchId: cafeMuhasebeBranchId || null,
        cafeMuhasebeBranchName: cafeMuhasebeBranchName || null,
      });

      const cafeSettings = await ApiService.getCafeSettings();
      setCafeUnregisteredStudentDepartment(cafeSettings.cafeUnregisteredStudentDepartment);
      setCafeGuestUserDepartment(cafeSettings.cafeGuestUserDepartment);
      setCafeMenuApiKey(cafeSettings.cafeMenuApiKey || '');
      setCafeMuhasebeBranchId(cafeSettings.cafeMuhasebeBranchId || null);
      setCafeMuhasebeBranchName(cafeSettings.cafeMuhasebeBranchName || '');

      showToast('Cafe ayarları başarıyla güncellendi', 'success');
    } catch (error: any) {
      console.error('Cafe ayarları güncellenirken hata:', error);
      showToast(error?.message || 'Cafe ayarları güncellenirken bir hata oluştu', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleFetchBranches = async () => {
    setBranchLoading(true);
    try {
      const response = await ApiService.getCafeMuhasebeBranches();

      const branchList = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.branches)
          ? response.data.branches
          : Array.isArray(response?.branches)
            ? response.branches
            : [];
      setAvailableBranches(branchList);

      if (!branchList.length) {
        showToast('Uygun şube bulunamadı', 'warning');
      }
    } catch (error: any) {
      console.error('Şubeler alınırken hata:', error);
      showToast(error?.message || 'Şube listesi alınamadı', 'error');
    } finally {
      setBranchLoading(false);
    }
  };

  const handleCheckHealth = async () => {
    setHealthChecking(true);
    try {
      const response = await ApiService.getCafeMuhasebeHealth();
      setHealthStatus(response);
      showToast('Bağlantı başarılı', 'success');
    } catch (error: any) {
      console.error('Katalog bağlantı testi hatası:', error);
      setHealthStatus(null);
      showToast(error?.message || 'Bağlantı kontrol edilemedi', 'error');
    } finally {
      setHealthChecking(false);
    }
  };

  if (loading) return <LoadingState />;

  const accent: SettingsFormAccent = 'amber';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5'>
            <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-amber-600"><path d="M17 8h1a4 4 0 1 1 0 8h-1" /><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z" /><line x1="6" x2="6" y1="2" y2="4" /><line x1="10" x2="10" y1="2" y2="4" /><line x1="14" x2="14" y1="2" y2="4" /></svg>
              Cafe Ayarları
            </h1>
            <p className='text-sm text-gray-600 mt-1'>Cafe kayıt sistemi için departman ve API key ayarlarını yapılandırın.</p>
            <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
          </div>

        <form onSubmit={handleSubmit} className='space-y-5'>
          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Kayıt departmanları</span>
            </div>
            <p className='text-sm text-gray-600 mb-6'>
              Cafe üzerinden kayıt olan kullanıcılar yaşlarına göre otomatik olarak bu departmanlara atanır.
            </p>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                  Öğrenciler (18 Yaş ve Altı)
                </label>
                <p className='text-xs text-gray-500 mb-2'>
                  18 yaş ve altındaki kullanıcılar bu departmana kaydedilir.
                </p>
                <DepartmentDropdown
                  value={cafeUnregisteredStudentDepartmentId}
                  onChange={(id) => {
                    setCafeUnregisteredStudentDepartmentId(id);
                    if (!id) setCafeUnregisteredStudentDepartment(null);
                  }}
                  onDepartmentSelect={setCafeUnregisteredStudentDepartment}
                  selectedDepartment={cafeUnregisteredStudentDepartment}
                  disabled={!hasUpdatePermission}
                  placeholder="Departman seçin..."
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                  Mezun (18 Yaş Üstü)
                </label>
                <p className='text-xs text-gray-500 mb-2'>
                  18 yaş üstü kullanıcılar bu departmana kaydedilir.
                </p>
                <DepartmentDropdown
                  value={cafeGuestUserDepartmentId}
                  onChange={(id) => {
                    setCafeGuestUserDepartmentId(id);
                    if (!id) setCafeGuestUserDepartment(null);
                  }}
                  onDepartmentSelect={setCafeGuestUserDepartment}
                  selectedDepartment={cafeGuestUserDepartment}
                  disabled={!hasUpdatePermission}
                  placeholder="Departman seçin..."
                />
              </div>
            </div>
          </div>

          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>API güvenliği</span>
            </div>
            <p className='text-sm text-gray-600 mb-6'>
              Bu API key, cafe kayıt sistemi endpoint'ine erişim için gereklidir. Güvenli bir yerde saklayın.
            </p>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Cafe Kayıt API Key
              </label>
              <div className='flex flex-wrap gap-2'>
                <div className='relative flex-1 min-w-[12rem]'>
                  <input
                    type={showRegistrationApiKey ? 'text' : 'password'}
                    value={cafeRegistrationApiKey}
                    onChange={(e) => setCafeRegistrationApiKey(e.target.value)}
                    disabled={!hasUpdatePermission}
                    className={`${settingsInputClass(accent)} pr-10 font-mono text-sm`}
                    placeholder='API key giriniz veya oluşturun...'
                  />
                  <button
                    type='button'
                    onClick={() => setShowRegistrationApiKey(!showRegistrationApiKey)}
                    className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                  >
                    {showRegistrationApiKey ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" x2="23" y1="1" y2="23" /></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                    )}
                  </button>
                </div>
                <button
                  type='button'
                  onClick={() => generateApiKey(setCafeRegistrationApiKey)}
                  disabled={!hasUpdatePermission}
                  className='px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                >
                  Oluştur
                </button>
                {cafeRegistrationApiKey && (
                  <button
                    type='button'
                    onClick={() => {
                      navigator.clipboard.writeText(cafeRegistrationApiKey);
                      showToast('API key kopyalandı', 'success');
                    }}
                    className='px-4 py-2.5 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm font-medium hover:bg-amber-100 transition-colors'
                  >
                    Kopyala
                  </button>
                )}
              </div>
              <p className='text-xs text-gray-500 mt-2'>
                Endpoint: <code className='bg-slate-100 px-1.5 py-0.5 rounded text-amber-800'>POST /external/register</code>
              </p>
            </div>
          </div>

          <div className={SETTINGS_SECTION}>
            <div className='mb-3 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
              <div>
                <span className={a.badge}>Menü bağlantısı</span>
                <p className='text-sm text-gray-600 mt-2'>
                  YES.Tools kullanıcıları için ürün menüsü ve fiyatlar bu bağlantı üzerinden gösterilir.
                </p>
              </div>
              <button
                type='button'
                onClick={handleCheckHealth}
                disabled={healthChecking}
                className='inline-flex shrink-0 items-center justify-center gap-2 px-4 py-2 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm font-medium hover:bg-emerald-100 disabled:opacity-50'
              >
                {healthChecking ? <Loader2 className='w-4 h-4 animate-spin' /> : <RefreshCcw className='w-4 h-4' />}
                Bağlantıyı Test Et
              </button>
            </div>

            <div className='bg-slate-50 rounded-xl border border-slate-200 p-4 sm:p-5 mb-6'>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                Cafe Menü API Key
              </label>
              <div className='flex flex-wrap gap-2'>
                <div className='relative flex-1 min-w-[12rem]'>
                  <input
                    type={showMenuApiKey ? 'text' : 'password'}
                    value={cafeMenuApiKey}
                    onChange={(e) => setCafeMenuApiKey(e.target.value)}
                    disabled={!hasUpdatePermission}
                    className={`${settingsInputClass(accent)} pr-10 font-mono text-sm`}
                    placeholder='Menü API key giriniz veya oluşturun...'
                  />
                  <button
                    type='button'
                    onClick={() => setShowMenuApiKey(!showMenuApiKey)}
                    className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                  >
                    {showMenuApiKey ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" x2="23" y1="1" y2="23" /></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                    )}
                  </button>
                </div>
                {cafeMenuApiKey && (
                  <button
                    type='button'
                    onClick={() => {
                      navigator.clipboard.writeText(cafeMenuApiKey);
                      showToast('API key kopyalandı', 'success');
                    }}
                    className='px-4 py-2.5 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm font-medium hover:bg-amber-100 transition-colors'
                  >
                    Kopyala
                  </button>
                )}
              </div>
              <p className='text-xs text-gray-500 mt-2'>
                Ürün menüsü ve fiyat listesi isteklerinde kullanılır.
              </p>
            </div>

            {healthStatus?.timestamp && (
              <div className='mb-6 flex items-center gap-2 text-sm text-emerald-700'>
                <CheckCircle2 className='w-4 h-4' />
                Son başarılı kontrol: {new Date(healthStatus.timestamp).toLocaleString('tr-TR')}
              </div>
            )}

            <div className='flex flex-wrap items-center gap-3 mb-4'>
              <button
                type='button'
                onClick={handleFetchBranches}
                disabled={branchLoading}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50 ${a.primaryBtn}`}
              >
                {branchLoading ? <Loader2 className='w-4 h-4 animate-spin' /> : <RefreshCcw className='w-4 h-4' />}
                Şubeleri Getir
              </button>
              {cafeMuhasebeBranchName && (
                <span className='text-sm text-gray-600'>
                  Seçili şube: <strong>{cafeMuhasebeBranchName}</strong>
                </span>
              )}
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>Şube</label>
              <select
                value={cafeMuhasebeBranchId || ''}
                onChange={(e) => {
                  const selected = availableBranches.find((branch) => branch.id === e.target.value);
                  setCafeMuhasebeBranchId(selected?.id || null);
                  setCafeMuhasebeBranchName(selected?.name || '');
                }}
                className={settingsInputClass(accent)}
              >
                <option value=''>Şube seçin...</option>
                {cafeMuhasebeBranchId && cafeMuhasebeBranchName && !availableBranches.some((branch) => branch.id === cafeMuhasebeBranchId) && (
                  <option value={cafeMuhasebeBranchId}>{cafeMuhasebeBranchName}</option>
                )}
                {availableBranches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}{branch.district || branch.province ? ` - ${[branch.district, branch.province].filter(Boolean).join(' / ')}` : ''}
                  </option>
                ))}
              </select>
              <p className='text-xs text-gray-500 mt-2'>
                Bu seçim yalnızca YES.Tools tarafındaki <strong>Cafe Menüsü</strong> modülünü etkiler.
              </p>
            </div>
          </div>

          <div className='sticky bottom-0 z-10 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0'>
            <div className='flex justify-end'>
              <button
                type='submit'
                disabled={!hasUpdatePermission || saving}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}
              >
                {saving ? (
                  <Loader2 className='w-4 h-4 animate-spin' />
                ) : (
                  <Save className='w-4 h-4' />
                )}
                {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
              </button>
            </div>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}

function RegistrationSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isRegistrationEnabled, setIsRegistrationEnabled] = useState(true);
  const [registrationPromptText, setRegistrationPromptText] = useState('');
  const [pendingApprovalDepartmentId, setPendingApprovalDepartmentId] = useState<string | null>(null);
  const [pendingApprovalDepartment, setPendingApprovalDepartment] = useState<{ id: string; name: string; color?: string } | null>(null);
  const [cafeUnregisteredStudentDepartmentId, setCafeUnregisteredStudentDepartmentId] = useState<string | null>(null);
  const [cafeGuestUserDepartmentId, setCafeGuestUserDepartmentId] = useState<string | null>(null);
  const [registrationDepartmentIds, setRegistrationDepartmentIds] = useState<string[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [data, cafeSettings] = await Promise.all([
          ApiService.getRegistrationSettings(),
          ApiService.getCafeSettings(),
        ]);
        setIsRegistrationEnabled(data.isRegistrationEnabled !== false);
        setRegistrationPromptText(data.registrationPromptText || '');
        setPendingApprovalDepartmentId(data.pendingApprovalDepartmentId || null);
        setPendingApprovalDepartment(data.pendingApprovalDepartment || null);
        setRegistrationDepartmentIds(data.registrationDepartmentIds || []);
        setCafeUnregisteredStudentDepartmentId(cafeSettings?.cafeUnregisteredStudentDepartmentId || null);
        setCafeGuestUserDepartmentId(cafeSettings?.cafeGuestUserDepartmentId || null);
      } catch (error) {
        console.error('Kayıt ayarları yüklenirken hata:', error);
        showToast('Kayıt ayarları yüklenirken bir hata oluştu', 'error');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [showToast]);

  const handleSave = async () => {
    if (!hasUpdatePermission) return;
    const hasCafeFallbackDepartments = Boolean(
      cafeUnregisteredStudentDepartmentId && cafeGuestUserDepartmentId
    );

    if (!pendingApprovalDepartmentId && !hasCafeFallbackDepartments) {
      showToast("Onay bekleyenler departmanı seçilmelidir veya Cafe ayarlarında 'Öğrenciler' ve 'Mezun' departmanları birlikte tanımlı olmalıdır.", 'warning');
      return;
    }

    setSaving(true);
    try {
      const response = await ApiService.updateRegistrationSettings({
        isRegistrationEnabled,
        registrationPromptText: registrationPromptText.trim(),
        pendingApprovalDepartmentId,
      });
      setPendingApprovalDepartmentId(response.pendingApprovalDepartmentId || null);
      setPendingApprovalDepartment(response.pendingApprovalDepartment || null);
      // Kayıtta görünecek departmanları da kaydet
      await ApiService.updateRegistrationDepartments(registrationDepartmentIds);
      showToast('Kayıt ayarları başarıyla güncellendi', 'success');
    } catch (error: any) {
      showToast(error?.message || 'Kayıt ayarları güncellenirken bir hata oluştu', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;

  const accent: SettingsFormAccent = 'sky';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5'>
            <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
              <UserCheck className='w-6 h-6 text-sky-600' />
              Kayıt Ayarları
            </h1>
            <p className='text-sm text-gray-600 mt-1'>Yeni kullanıcı kayıtlarını açıp kapatabilirsiniz.</p>
            <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
          </div>

        <div className='space-y-5'>
          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Kayıt anahtarı</span>
            </div>
            <div className='flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200'>
              <div className='flex items-center gap-3'>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isRegistrationEnabled ? 'bg-sky-100 text-sky-700' : 'bg-red-100 text-red-500'
                  }`}>
                  <UserCheck className='w-5 h-5' />
                </div>
                <div>
                  <p className='font-semibold text-gray-800'>Kayıt Sistemi</p>
                  <p className='text-sm text-gray-500'>
                    {isRegistrationEnabled
                      ? 'Yeni kullanıcılar /register sayfasından kayıt olabilir.'
                      : 'Kayıt kapalı. Yeni kullanıcılar kayıt olamaz.'}
                  </p>
                </div>
              </div>
              <button
                type='button'
                onClick={() => hasUpdatePermission && setIsRegistrationEnabled(!isRegistrationEnabled)}
                disabled={!hasUpdatePermission}
                title={hasUpdatePermission ? '' : 'Bu ayarı değiştirmek için yetkiniz yok'}
                className={`relative flex w-14 h-7 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 ${!hasUpdatePermission ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                  } ${isRegistrationEnabled ? 'bg-sky-600' : 'bg-gray-300'
                  }`}
                aria-pressed={isRegistrationEnabled}
              >
                <span
                  className={`absolute left-1 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${isRegistrationEnabled ? 'translate-x-7' : 'translate-x-0'
                    }`}
                />
              </button>
            </div>
          </div>

          <div className={`p-4 rounded-xl border text-sm shadow-sm ${isRegistrationEnabled
            ? 'bg-sky-50 border-sky-200 text-sky-900'
            : 'bg-red-50 border-red-200 text-red-800'
            }`}>
            {isRegistrationEnabled ? (
              <>
                <p className='font-semibold mb-1'>Kayıt açık</p>
                <ul className='list-disc list-inside space-y-1 text-sky-800/90'>
                  <li>Onay bekleyenler departmanı seçilirse yeni kayıtlar doğrudan bu departmana atanır</li>
                  <li>Onay bekleyenler boş bırakılırsa ve Cafe ayarlarında iki departman da seçiliyse yaşa göre otomatik atama yapılır</li>
                  <li>Cafe fallback için gerekli alanlar: <strong>Öğrenciler</strong> ve <strong>Mezun</strong></li>
                </ul>
              </>
            ) : (
              <>
                <p className='font-semibold mb-1'>Kayıt kapalı</p>
                <p className='text-red-700'>Giriş sayfasında &quot;Kaydol&quot; butonu gizlenecek. Mevcut kullanıcılar etkilenmez.</p>
              </>
            )}
          </div>

          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Onay bekleyenler</span>
            </div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Onay Bekleyenler Departmanı
            </label>
            <DepartmentDropdown
              value={pendingApprovalDepartmentId}
              onChange={(departmentId) => {
                setPendingApprovalDepartmentId(departmentId);
                if (!departmentId) setPendingApprovalDepartment(null);
              }}
              onDepartmentSelect={setPendingApprovalDepartment}
              selectedDepartment={pendingApprovalDepartment}
              disabled={!hasUpdatePermission}
              placeholder='Departman Seçiniz...'
            />
            <p className='text-xs text-gray-500 mt-2'>
              Yeni kayıt olan kullanıcılar doğrudan bu departmana atanır ve onay/review süreçlerinde işlenir.
            </p>
          </div>

          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Kayıtta görünecek departmanlar</span>
            </div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Kayıt Ekranında Seçilebilecek Departmanlar
            </label>
            <div className='rounded-xl border border-slate-200 overflow-hidden max-h-72 overflow-y-auto'>
              <DepartmentTreeSelect
                value={registrationDepartmentIds}
                onChange={setRegistrationDepartmentIds}
                disabled={!hasUpdatePermission}
                multiple={true}
              />
            </div>
            <p className='text-xs text-gray-500 mt-2'>
              Seçtiğiniz departmanlar kayıt ekranında liste olarak görünür; kullanıcı birini seçip o departmana kayıt olur. Boş bırakılırsa eski sınıf-seçimli kayıt akışı geçerli olur.
            </p>
          </div>

          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Giriş metni</span>
            </div>
            <label className='block text-sm font-medium text-gray-700 mb-2'>
              Kayıt Olma Yönlendirme Metni
            </label>
            <input
              type='text'
              value={registrationPromptText}
              onChange={(e) => setRegistrationPromptText(e.target.value)}
              disabled={!hasUpdatePermission}
              placeholder='Ör: Hesabın yok mu?'
              className={settingsInputClass(accent)}
            />
            <p className='text-xs text-gray-500 mt-2'>
              Giriş sayfasında kayıt olma linki öncesinde görünecek metin (Boş bırakılırsa &quot;Hesabın yok mu?&quot; gösterilir).
            </p>
          </div>

          <div className='sticky bottom-0 z-10 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0'>
            <div className='flex justify-end'>
              <button
                type='button'
                onClick={handleSave}
                disabled={!hasUpdatePermission || saving}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}
              >
                {saving ? <Loader2 className='w-4 h-4 animate-spin' /> : <Save className='w-4 h-4' />}
                {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

function N8nSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [n8nChatWebhookUrl, setN8nChatWebhookUrl] = useState('');
  const [n8nLatexExtractorWebhookUrl, setN8nLatexExtractorWebhookUrl] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const n8nSettings = await ApiService.getN8nSettings();

        setN8nChatWebhookUrl(n8nSettings.n8nChatWebhookUrl || '');
        setN8nLatexExtractorWebhookUrl(n8nSettings.n8nLatexExtractorWebhookUrl || '');
      } catch (error) {
        console.error('n8n ayarları yüklenirken hata:', error);
        showToast('n8n ayarları yüklenirken bir hata oluştu', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [showToast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasUpdatePermission) return;

    setSaving(true);
    try {
      await ApiService.updateN8nSettings({
        n8nChatWebhookUrl: n8nChatWebhookUrl || null,
        n8nLatexExtractorWebhookUrl: n8nLatexExtractorWebhookUrl || null,
      });

      showToast('n8n ayarları başarıyla güncellendi', 'success');
    } catch (error: any) {
      console.error('n8n ayarları güncellenirken hata:', error);
      showToast(error?.message || 'n8n ayarları güncellenirken bir hata oluştu', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;

  const accent: SettingsFormAccent = 'indigo';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5'>
            <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
              <ExternalLink className='w-6 h-6 text-indigo-600' />
              Yapay Zeka Ayarları
            </h1>
            <p className='text-sm text-gray-600 mt-1'>n8n webhook bağlantılarını buradan yönetebilirsiniz.</p>
            <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
          </div>

        <form onSubmit={handleSubmit} className='space-y-5'>
          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Webhook adresleri</span>
            </div>
            <div className='grid grid-cols-1 gap-6'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                  <ExternalLink className='w-4 h-4 text-indigo-600' />
                  Chat Webhook URL
                </label>
                <input
                  type='text'
                  value={n8nChatWebhookUrl}
                  onChange={(e) => setN8nChatWebhookUrl(e.target.value)}
                  disabled={!hasUpdatePermission}
                  className={settingsInputClass(accent)}
                  placeholder='https://...'
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                  <ExternalLink className='w-4 h-4 text-indigo-600' />
                  LaTeX Extractor Webhook URL
                </label>
                <input
                  type='text'
                  value={n8nLatexExtractorWebhookUrl}
                  onChange={(e) => setN8nLatexExtractorWebhookUrl(e.target.value)}
                  disabled={!hasUpdatePermission}
                  className={settingsInputClass(accent)}
                  placeholder='https://...'
                />
              </div>
            </div>
          </div>

          <div className='sticky bottom-0 z-10 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0'>
            <div className='flex justify-end'>
              <button
                type='submit'
                disabled={!hasUpdatePermission || saving}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}
              >
                {saving ? (
                  <Loader2 className='w-4 h-4 animate-spin' />
                ) : (
                  <Save className='w-4 h-4' />
                )}
                {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
              </button>
            </div>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}

const normalizeAiTeacherModels = (models?: AiTeacherAudioModel[]): AiTeacherAudioModel[] => {
  const source = models?.length ? models : AI_TEACHER_AUDIO_MODELS;

  return source.map((model) => {
    const fallback = AI_TEACHER_AUDIO_MODELS.find((item) => item.id === model.id);
    const mode: AiTeacherAudioModel['mode'] =
      model.mode === 'live' ? 'live' : model.mode === 'browser' ? 'browser' : model.mode === 'none' ? 'none' : 'tts';

    return {
      ...fallback,
      ...model,
      mode,
      description: model.description || fallback?.description,
    };
  });
};

function AITeacherSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [audioModel, setAudioModel] = useState(AI_TEACHER_AUDIO_MODELS[0].id);
  const [supportedModels, setSupportedModels] = useState<AiTeacherAudioModel[]>(AI_TEACHER_AUDIO_MODELS);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const settings = await ApiService.getAITeacherSettings();
        const models = normalizeAiTeacherModels(settings.supportedAudioModels);

        setSupportedModels(models);
        setAudioModel(settings.aiTeacherAudioModel || models[0]?.id || AI_TEACHER_AUDIO_MODELS[0].id);
      } catch (error) {
        console.error('AI Öğretmenim ayarları yüklenirken hata:', error);
        showToast('AI Öğretmenim ayarları yüklenirken bir hata oluştu', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [showToast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasUpdatePermission) return;

    setSaving(true);
    try {
      const response = await ApiService.updateAITeacherSettings({
        aiTeacherAudioModel: audioModel,
      });

      if (response?.aiTeacherAudioModel) setAudioModel(response.aiTeacherAudioModel);
      showToast('AI Öğretmenim ayarları başarıyla güncellendi', 'success');
    } catch (error: any) {
      console.error('AI Öğretmenim ayarları güncellenirken hata:', error);
      showToast(error?.message || 'AI Öğretmenim ayarları güncellenirken bir hata oluştu', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;

  const selectedModel = supportedModels.find((model) => model.id === audioModel);
  const accent: SettingsFormAccent = 'cyan';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className="mb-5">
            <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
              <Bot className="w-6 h-6 text-cyan-600" />
              AI Öğretmenim
            </h1>
            <p className="text-sm text-gray-600 mt-1">Eksikliklerim alanındaki AI öğretmenin ses modelini buradan seçebilirsiniz.</p>
            <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className={SETTINGS_SECTION}>
              <div className="mb-3">
                <span className={a.badge}>Ses modeli</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {supportedModels.map((model) => {
                  const isSelected = audioModel === model.id;
                  const Icon = model.mode === 'live' ? Radio : Volume2;

                  return (
                    <button
                      key={model.id}
                      type="button"
                      aria-pressed={isSelected}
                      disabled={!hasUpdatePermission || saving}
                      onClick={() => setAudioModel(model.id)}
                      className={`text-left p-4 rounded-xl border transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                        isSelected
                          ? "border-cyan-300 bg-cyan-50 shadow-sm"
                          : "border-slate-200 bg-white hover:border-cyan-200 hover:bg-cyan-50/40"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isSelected ? "bg-cyan-600 text-white" : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900">{model.label}</p>
                              <p className="text-xs text-gray-500 mt-1 break-all">{model.id}</p>
                            </div>
                            {isSelected && <CheckCircle2 className="w-5 h-5 text-cyan-600 flex-shrink-0" />}
                          </div>
                          {model.description ? <p className="text-sm text-gray-600 mt-3">{model.description}</p> : null}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {selectedModel?.mode === "live" && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  Live API modeli her ses üretiminde anlık bağlantı açar; gecikme ve ses karakteri TTS modelinden farklı olabilir.
                </div>
              )}
            </div>

            <div className="sticky bottom-0 z-10 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0">
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={!hasUpdatePermission || saving}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? "Kaydediliyor..." : "Ayarları Kaydet"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function SmsSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isCustomSms, setIsCustomSms] = useState(false);
  const [smsApiKey, setSmsApiKey] = useState('');
  const [smsApiSecret, setSmsApiSecret] = useState('');
  const [smsSenderName, setSmsSenderName] = useState('');
  const [showApiSecret, setShowApiSecret] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        const data = await ApiService.getSmsSettings();
        setIsCustomSms(data.isCustomSms || false);
        setSmsApiKey(data.smsApiKey || '');
        setSmsApiSecret(data.smsApiSecret || '');
        setSmsSenderName(data.smsSenderName || '');
      } catch (error) {
        console.error('SMS ayarları yüklenirken hata:', error);

        showToast('SMS ayarları yüklenirken bir hata oluştu', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [showToast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasUpdatePermission) return;

    if (isCustomSms && (!smsApiKey || !smsApiSecret || !smsSenderName)) {
      showToast('SMS doğrulama aktifse tüm alanlar zorunludur', 'warning');

      return;
    };

    setSaving(true);

    try {
      await ApiService.updateSmsSettings({
        isCustomSms,
        smsProvider: 'mutlucell',
        smsApiKey: smsApiKey || null,
        smsApiSecret: smsApiSecret || null,
        smsSenderName: smsSenderName || null,
      });

      showToast('SMS ayarları başarıyla güncellendi', 'success');
    } catch (error: any) {
      showToast(error?.message || 'SMS ayarları güncellenirken bir hata oluştu', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;

  const accent: SettingsFormAccent = 'rose';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5'>
            <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
              <MessageSquare className='w-6 h-6 text-rose-600' />
              SMS Ayarları
            </h1>
            <p className='text-sm text-gray-600 mt-1'>SMS doğrulama ve gönderim ayarlarını buradan yapılandırın. Sağlayıcı: Mutlucell</p>
            <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
          </div>

        <form onSubmit={handleSubmit} className='space-y-5'>
          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Doğrulama</span>
            </div>
            <div className='flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200'>
              <div className='flex items-center gap-3'>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isCustomSms ? 'bg-rose-100 text-rose-700' : 'bg-gray-200 text-gray-500'
                  }`}>
                  <MessageSquare className='w-5 h-5' />
                </div>
                <div>
                  <p className='font-semibold text-gray-800'>SMS Doğrulama</p>
                  <p className='text-sm text-gray-500'>
                    {isCustomSms
                      ? 'Yeni kayıt olan kullanıcılardan SMS ile telefon doğrulaması istenir.'
                      : 'SMS doğrulama kapalı. Kayıt için telefon doğrulaması gerekmez.'}
                  </p>
                </div>
              </div>
              <button
                type='button'
                onClick={() => hasUpdatePermission && setIsCustomSms(!isCustomSms)}
                disabled={!hasUpdatePermission}
                className={`relative flex w-14 h-7 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 ${!hasUpdatePermission ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                  } ${isCustomSms ? 'bg-rose-600' : 'bg-gray-300'
                  }`}
                aria-pressed={isCustomSms}
              >
                <span
                  className={`absolute left-1 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${isCustomSms ? 'translate-x-7' : 'translate-x-0'
                    }`}
                />
              </button>
            </div>
          </div>

          {isCustomSms && (
            <div className={SETTINGS_SECTION}>
              <div className='mb-3'>
                <span className={a.badge}>Mutlucell API</span>
              </div>
              <p className='text-sm text-gray-600 mb-6'>
                Mutlucell hesabınızdan aldığınız API bilgilerini girin. Bu bilgiler SMS gönderimi için kullanılır.
              </p>

              <div className='grid grid-cols-1 gap-6'>
                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                    <MessageSquare className='w-4 h-4 text-rose-600' />
                    SMS Başlığı (Originatör)
                  </label>
                  <input
                    type='text'
                    value={smsSenderName}
                    onChange={(e) => setSmsSenderName(e.target.value)}
                    disabled={!hasUpdatePermission}
                    className={settingsInputClass(accent)}
                    placeholder='Mutlucell panelinde onaylanan başlık'
                  />
                  <p className='text-xs text-gray-500 mt-1'>SMS alıcılarına görünen gönderici adı. Mutlucell panelinde onaylanmış olmalıdır.</p>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-rose-600"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                    SMS Kullanıcı Adı
                  </label>
                  <input
                    type='text'
                    value={smsApiKey}
                    onChange={(e) => setSmsApiKey(e.target.value)}
                    disabled={!hasUpdatePermission}
                    className={settingsInputClass(accent)}
                    placeholder='Mutlucell kullanıcı adınız'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                    <Lock className='w-4 h-4 text-rose-600' />
                    SMS API Key / Şifre
                  </label>
                  <div className='relative'>
                    <input
                      type={showApiSecret ? 'text' : 'password'}
                      value={smsApiSecret}
                      onChange={(e) => setSmsApiSecret(e.target.value)}
                      disabled={!hasUpdatePermission}
                      className={`${settingsInputClass(accent)} pr-10 font-mono text-sm`}
                      placeholder='Mutlucell API key veya şifreniz'
                    />
                    <button
                      type='button'
                      onClick={() => setShowApiSecret(!showApiSecret)}
                      className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                    >
                      {showApiSecret ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" x2="23" y1="1" y2="23" /></svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                      )}
                    </button>
                  </div>
                  <p className='text-xs text-gray-500 mt-1'>Mutlucell panelinden oluşturduğunuz API key veya hesap şifreniz.</p>
                </div>
              </div>
            </div>
          )}

          {isCustomSms && (
            <div className='bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-900 shadow-sm'>
              <p className='font-semibold mb-2'>SMS doğrulama kuralları</p>
              <ul className='list-disc list-inside space-y-1 text-rose-800/90'>
                <li>Kayıt olurken kullanıcılardan telefon numarası ve SMS doğrulaması istenir</li>
                <li>Her <strong>5 dakikada bir</strong> yeni doğrulama kodu talep edilebilir</li>
                <li>Günde en fazla <strong>3 kez</strong> yeni kod gönderilebilir</li>
                <li>Kullanıcı uygulamayı kapatıp tekrar girerse <strong>eski kod geçerli</strong> kalır</li>
                <li>Doğrulama yapılmadan giriş yapılırsa tekrar SMS doğrulama ekranı gösterilir</li>
              </ul>
            </div>
          )}

          <div className='sticky bottom-0 z-10 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0'>
            <div className='flex justify-end'>
              <button
                type='submit'
                disabled={!hasUpdatePermission || saving}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}
              >
                {saving ? <Loader2 className='w-4 h-4 animate-spin' /> : <Save className='w-4 h-4' />}
                {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
              </button>
            </div>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}

function AnnouncementSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();
  const { settingsData, fetching, setSettingsData } = useSystemSettings();

  const [announcementCount, setAnnouncementCount] = useState<number>(3);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (settingsData) setAnnouncementCount(settingsData.announcementCount ?? 3);
  }, [settingsData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!hasUpdatePermission) return;
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('announcementCount', announcementCount.toString());

      const response = await ApiService.updateSettings(formData);
      if (!response) throw new Error('Kayıt başarısız');

      setSettingsData(response);
      showToast('Duyuru ayarları başarıyla güncellendi', 'success');
    } catch (error: any) {
      console.error('Ayarlar güncellenirken hata:', error);

      showToast(error?.message || 'Ayarlar güncellenirken bir hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <LoadingState />;

  const accent: SettingsFormAccent = 'orange';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5'>
            <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
              <Bell className='w-6 h-6 text-orange-600' />
              Duyuru Ayarları
            </h1>
            <p className='text-sm text-gray-600 mt-1'>Sistemde gösterilecek son duyuru sayısını buradan yönetebilirsiniz.</p>
            <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
          </div>

        <form onSubmit={handleSubmit} className='space-y-5'>
          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Dashboard görünümü</span>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2'>
                  <Bell className='w-4 h-4 text-orange-600' />
                  Gösterilecek Duyuru Sayısı
                </label>
                <input
                  type='number'
                  min='1'
                  value={announcementCount}
                  onChange={(e) => setAnnouncementCount(Number(e.target.value))}
                  disabled={!hasUpdatePermission}
                  className={settingsInputClass(accent)}
                  placeholder='Varsayılan: 3'
                />
                <p className='text-xs text-gray-500 mt-2'>
                  Kullanıcıların dashboard ekranında görebileceği maksimum &quot;son duyuru&quot; sayısını belirler (Varsayılan 3).
                </p>
              </div>
            </div>
          </div>

          <div className='sticky bottom-0 z-10 -mx-1 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0'>
            <div className='flex justify-end'>
              <button
                type='submit'
                disabled={!hasUpdatePermission || loading}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}
              >
                {loading ? (
                  <Loader2 className='w-4 h-4 animate-spin' />
                ) : (
                  <Save className='w-4 h-4' />
                )}
                {loading ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
              </button>
            </div>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}

function PaymentSettingsTab() {
  const { showToast } = useModal();
  const [data, setData] = useState<any>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settings, paymentOrders] = await Promise.all([
        ApiService.getPaymentAdminSettings(),
        ApiService.getPaymentOrders().catch(() => []),
      ]);
      setData({
        ...settings,
        mappings: (settings.mappings || []).map((item: any) => ({
          sourceDepartmentId: item.sourceDepartmentId,
          targetDepartmentId: item.targetDepartmentId,
        })),
      });
      setOrders(paymentOrders);
    } catch (error: any) {
      showToast(error?.message || 'Ödeme ayarları yüklenemedi', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await ApiService.updatePaymentAdminSettings({
        paymentSystemEnabled: data.paymentSystemEnabled,
        paidModuleIds: data.paidModuleIds,
        packagePrices: data.packagePrices,
        enabledProviders: data.enabledProviders,
        mappings: data.mappings,
      });
      setData({
        ...updated,
        mappings: (updated.mappings || []).map((item: any) => ({
          sourceDepartmentId: item.sourceDepartmentId,
          targetDepartmentId: item.targetDepartmentId,
        })),
      });
      showToast('Ödeme ayarları kaydedildi', 'success');
    } catch (error: any) {
      showToast(error?.message || 'Ödeme ayarları kaydedilemedi', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) return <LoadingState />;

  const menuItemsById = new Map(defaultMenuItems.map((item) => [item.id, item]));

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5 flex flex-wrap items-start justify-between gap-3'>
            <div>
              <h1 className={SETTINGS_PAGE_TITLE_CLASS}><CreditCard className='h-6 w-6 text-violet-600' />Ödeme ve Ücretli Modüller</h1>
              <p className='mt-1 text-sm text-gray-600'>Paketleri, sağlayıcıları ve departman geçişlerini yönetin.</p>
              <div className='mt-3 h-1 w-20 rounded-full bg-violet-200' />
            </div>
            <button onClick={save} disabled={saving} className='inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50'>
              {saving ? <Loader2 className='h-4 w-4 animate-spin' /> : <Save className='h-4 w-4' />}Kaydet
            </button>
          </div>

          <div className='space-y-5'>
            <section className={SETTINGS_SECTION}>
              <label className='flex items-center justify-between gap-4'>
                <span><span className='block font-semibold text-slate-900'>Ödeme Sistemi</span><span className='text-sm text-slate-500'>YES.Tools ücretli modül akışını etkinleştirir.</span></span>
                <input type='checkbox' checked={Boolean(data.paymentSystemEnabled)} onChange={(event) => setData((prev: any) => ({ ...prev, paymentSystemEnabled: event.target.checked }))} className='h-5 w-5 accent-violet-600' />
              </label>
            </section>

            <section className={SETTINGS_SECTION}>
              <h2 className='font-semibold text-slate-900'>Ücretli Modüller</h2>
              <p className='mt-1 text-sm text-slate-500'>Kullanıcıların ödeme yapmadan önce kilitli göreceği modülleri seçin.</p>
              <div className='mt-4 grid gap-3 sm:grid-cols-2'>
                {data.modules.map((module: any) => {
                  const checked = data.paidModuleIds.includes(module.id);
                  const menuItem = menuItemsById.get(module.id);
                  const ModuleIcon = menuItem?.icon || Lock;
                  const moduleColor = menuItem?.color || '#64748b';
                  return (
                    <label key={module.id} className={`group flex cursor-pointer items-center gap-3 rounded-xl border p-3.5 transition ${checked ? 'border-violet-300 bg-violet-50 shadow-sm ring-1 ring-violet-100' : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'}`}>
                      <span className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-white shadow-sm' style={{ backgroundColor: moduleColor }}>
                        <ModuleIcon className='h-5 w-5' />
                      </span>
                      <span className='min-w-0 flex-1'>
                        <span className='block truncate text-sm font-semibold text-slate-900'>{module.label}</span>
                        <span className='block truncate text-xs text-slate-500'>{module.path}</span>
                      </span>
                      <span className={`flex h-6 w-6 items-center justify-center rounded-full border transition ${checked ? 'border-violet-600 bg-violet-600 text-white' : 'border-slate-300 bg-white text-transparent'}`}>
                        <CheckCircle2 className='h-4 w-4' />
                      </span>
                      <input type='checkbox' checked={checked} onChange={(event) => setData((prev: any) => ({
                        ...prev,
                        paidModuleIds: event.target.checked ? [...prev.paidModuleIds, module.id] : prev.paidModuleIds.filter((id: string) => id !== module.id),
                      }))} className='sr-only' />
                    </label>
                  );
                })}
              </div>
            </section>

            <section className={SETTINGS_SECTION}>
              <h2 className='font-semibold text-slate-900'>Paket Fiyatları</h2>
              <div className='mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                {data.packages.map((item: any) => (
                  <label key={item.id} className='text-sm font-medium text-slate-700'>
                    {item.label}
                    <div className='mt-2 flex overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm transition focus-within:border-violet-300 focus-within:bg-white focus-within:ring-4 focus-within:ring-violet-50'>
                      <input
                        type='text'
                        inputMode='decimal'
                        value={data.packagePrices[item.id] ?? 0}
                        onChange={(event) => {
                          const normalized = event.target.value.replace(',', '.').replace(/[^0-9.]/g, '');
                          setData((prev: any) => ({
                            ...prev,
                            packagePrices: { ...prev.packagePrices, [item.id]: normalized },
                          }));
                        }}
                        className='min-w-0 flex-1 bg-transparent px-3.5 py-2.5 text-sm text-slate-800 outline-none'
                      />
                      <span className='flex items-center border-l border-slate-200 bg-slate-100 px-3 text-xs font-bold tracking-wide text-slate-500'>TRY</span>
                    </div>
                  </label>
                ))}
              </div>
            </section>

            <section className={SETTINGS_SECTION}>
              <h2 className='font-semibold text-slate-900'>Ödeme Sağlayıcıları</h2>
              <div className='mt-4 grid gap-3 sm:grid-cols-2'>
                {data.providers.map((item: any) => {
                  const enabled = data.enabledProviders.includes(item.id);
                  const providerMeta = getPaymentProviderMeta(item.id);
                  return (
                    <label key={item.id} className={`flex min-h-[88px] items-center justify-between gap-4 rounded-xl border p-4 transition ${enabled ? 'border-emerald-300 bg-emerald-50 shadow-sm' : 'border-slate-200 bg-white'} ${item.configured ? 'cursor-pointer' : 'cursor-not-allowed opacity-75'}`}>
                      <span className='flex min-w-0 items-center gap-4'>
                        <span className='flex h-12 w-32 flex-shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white px-3'>
                          <img src={providerMeta.logo} alt={providerMeta.label} className={`${providerMeta.logoClassName} max-w-full object-contain`} />
                        </span>
                        <span className='min-w-0'>
                          <span className='block font-semibold text-slate-900'>{providerMeta.label}</span>
                          <span className={`block text-xs ${item.configured ? 'text-emerald-600' : 'text-red-600'}`}>{item.configured ? 'Env yapılandırması hazır.' : 'Env bilgileri eksik.'}</span>
                        </span>
                      </span>
                      <input type='checkbox' disabled={!item.configured} checked={enabled} onChange={(event) => setData((prev: any) => ({
                        ...prev,
                        enabledProviders: event.target.checked ? [...prev.enabledProviders, item.id] : prev.enabledProviders.filter((id: string) => id !== item.id),
                      }))} className='h-5 w-5 accent-emerald-600' />
                    </label>
                  );
                })}
              </div>
            </section>

            <section className={SETTINGS_SECTION}>
              <div className='flex items-center justify-between gap-3'>
                <div><h2 className='font-semibold text-slate-900'>Departman Eşlemeleri</h2><p className='text-sm text-slate-500'>Ödeme yapan kullanıcı kaynak departmandan hedef departmana taşınır.</p></div>
                <button onClick={() => setData((prev: any) => ({ ...prev, mappings: [...prev.mappings, { sourceDepartmentId: '', targetDepartmentId: '' }] }))} className='inline-flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white'><Plus className='h-3.5 w-3.5' />Eşleme Ekle</button>
              </div>
              <div className='mt-4 space-y-3'>
                {data.mappings.map((mapping: any, index: number) => (
                  <div key={index} className='grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 md:grid-cols-[1fr_1fr_auto]'>
                    <DepartmentSelector value={mapping.sourceDepartmentId} onChange={(value) => setData((prev: any) => ({ ...prev, mappings: prev.mappings.map((item: any, itemIndex: number) => itemIndex === index ? { ...item, sourceDepartmentId: value } : item) }))} placeholder='Kaynak departman' />
                    <DepartmentSelector value={mapping.targetDepartmentId} onChange={(value) => setData((prev: any) => ({ ...prev, mappings: prev.mappings.map((item: any, itemIndex: number) => itemIndex === index ? { ...item, targetDepartmentId: value } : item) }))} placeholder='Hedef departman' excludeIds={mapping.sourceDepartmentId ? [mapping.sourceDepartmentId] : []} />
                    <button onClick={() => setData((prev: any) => ({ ...prev, mappings: prev.mappings.filter((_: any, itemIndex: number) => itemIndex !== index) }))} className='flex h-10 w-10 items-center justify-center rounded-lg text-red-600 hover:bg-red-50'><Trash2 className='h-4 w-4' /></button>
                  </div>
                ))}
              </div>
            </section>

            <section className={SETTINGS_SECTION}>
              <h2 className='font-semibold text-slate-900'>Son Ödeme Kayıtları</h2>
              <div className='mt-4 overflow-x-auto'>
                <table className='w-full min-w-[720px] text-left text-sm'>
                  <thead className='border-b border-slate-200 text-xs uppercase text-slate-500'><tr><th className='py-2'>Kullanıcı</th><th>Sağlayıcı</th><th>Paket</th><th>Tutar</th><th>Durum</th><th>Tarih</th></tr></thead>
                  <tbody>
                    {orders.slice(0, 50).map((order: any) => (
                      <tr key={order.id} className='border-b border-slate-100'>
                        <td className='py-3 font-medium'>{order.user ? `${order.user.firstName} ${order.user.lastName}` : order.userId}</td>
                        <td className='capitalize'>{order.provider}</td><td>{order.packageId}</td>
                        <td>{Number(order.paidAmount).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</td>
                        <td>{order.status}</td><td>{new Date(order.createdAt).toLocaleString('tr-TR')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExamDatesSettingsTab() {
  const hasUpdatePermission = checkPermission('setting_update');
  const { showToast } = useModal();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [examDates, setExamDates] = useState<ExamDateItem[]>([]);
  const [pendingDepartmentIds, setPendingDepartmentIds] = useState<Record<string, string | null>>({});
  const [departmentNames, setDepartmentNames] = useState<Record<string, string>>({});
  const [allDepartments, setAllDepartments] = useState<ExamDepartmentNode[]>([]);

  const normalizeExamDate = useCallback((exam: any): ExamDateItem => {
    const departmentIds = Array.isArray(exam?.departmentIds)
      ? [...new Set((exam.departmentIds as any[]).map((id) => String(id).trim()).filter(Boolean))] as string[]
      : [];

    return {
      id: String(exam?.id || ''),
      name: typeof exam?.name === 'string' ? exam.name : '',
      date: typeof exam?.date === 'string' ? exam.date : '',
      departmentIds,
      allDepartments: departmentIds.length === 0,
    };
  }, []);

  const collapseDepartmentIdsForDisplay = useCallback((departmentIds: string[], departments: ExamDepartmentNode[]) => {
    if (!departmentIds.length || !departments.length) {
      return departmentIds;
    }

    const normalizedIds = [...new Set(departmentIds.map((id) => String(id).trim()).filter(Boolean))];
    const selectedIds = new Set(normalizedIds);
    const departmentMap = new Map(departments.map((department) => [String(department.id), department]));

    return normalizedIds.filter((departmentId) => {
      const visited = new Set<string>();
      let currentParentId = departmentMap.get(departmentId)?.parentId ? String(departmentMap.get(departmentId)?.parentId) : null;

      while (currentParentId && !visited.has(currentParentId)) {
        if (selectedIds.has(currentParentId)) {
          return false;
        }

        visited.add(currentParentId);
        const parentDepartment = departmentMap.get(currentParentId);
        currentParentId = parentDepartment?.parentId ? String(parentDepartment.parentId) : null;
      }

      return true;
    });
  }, []);

  const expandDepartmentIdsForSave = useCallback((departmentIds: string[], departments: ExamDepartmentNode[]) => {
    if (!departmentIds.length || !departments.length) {
      return [...new Set(departmentIds.map((id) => String(id).trim()).filter(Boolean))];
    }

    const normalizedIds = [...new Set(departmentIds.map((id) => String(id).trim()).filter(Boolean))];
    const childrenMap = new Map<string | null, string[]>();

    departments.forEach((department) => {
      const parentKey = department.parentId ? String(department.parentId) : null;
      const siblings = childrenMap.get(parentKey) || [];
      siblings.push(String(department.id));
      childrenMap.set(parentKey, siblings);
    });

    const expandedIds = new Set<string>();

    const addDepartmentAndChildren = (departmentId: string) => {
      if (expandedIds.has(departmentId)) {
        return;
      }

      expandedIds.add(departmentId);

      const childIds = childrenMap.get(departmentId) || [];
      childIds.forEach(addDepartmentAndChildren);
    };

    normalizedIds.forEach(addDepartmentAndChildren);

    return Array.from(expandedIds);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [examDateRes, departmentRes] = await Promise.all([
          ApiService.getExamDates(),
          ApiService.searchAllDepartments({ sortBy: 'name', sortOrder: 'ASC' }),
        ]);

        const departmentRows = (departmentRes?.data || departmentRes || [])
          .filter((department: any) => !department?.deletedAt)
          .map((department: any) => ({
            id: String(department.id),
            name: department?.name || String(department.id),
            parentId: department?.parentId ? String(department.parentId) : null,
          })) as ExamDepartmentNode[];

        setAllDepartments(departmentRows);

        const normalizedExamDates = (examDateRes.examDates || []).map((exam: any) => {
          const normalized = normalizeExamDate(exam);
          const collapsedDepartmentIds = collapseDepartmentIdsForDisplay(normalized.departmentIds, departmentRows);

          return {
            ...normalized,
            departmentIds: collapsedDepartmentIds,
            allDepartments: collapsedDepartmentIds.length === 0,
          };
        });
        setExamDates(normalizedExamDates);

        setDepartmentNames(
          Object.fromEntries(departmentRows.map((department) => [department.id, department.name]))
        );
      } catch (error) {
        console.error('Sınav tarihleri yüklenirken hata:', error);
        showToast('Sınav tarihleri yüklenirken bir hata oluştu', 'error');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [collapseDepartmentIdsForDisplay, normalizeExamDate, showToast]);

  const generateId = () => `exam_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

  const handleAdd = () => {
    setExamDates(prev => [...prev, { id: generateId(), name: '', date: '', departmentIds: [], allDepartments: false }]);
  };

  const handleChange = (id: string, field: 'name' | 'date', value: string) => {
    setExamDates(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));
  };

  const handleDepartmentAdd = (examId: string, departmentId: string) => {
    if (!departmentId) return;
    setExamDates(prev => prev.map((exam) => (
      exam.id === examId
        ? {
          ...exam,
          allDepartments: false,
          departmentIds: collapseDepartmentIdsForDisplay(
            exam.departmentIds.includes(departmentId)
              ? exam.departmentIds
              : [...exam.departmentIds, departmentId],
            allDepartments
          ),
        }
        : exam
    )));
    setPendingDepartmentIds(prev => ({ ...prev, [examId]: null }));
    const selectedDepartment = allDepartments.find((department) => department.id === departmentId);
    setDepartmentNames((prev) => ({
      ...prev,
      ...(selectedDepartment?.name ? { [departmentId]: selectedDepartment.name } : {}),
    }));
  };

  const handleDepartmentRemove = (examId: string, departmentId: string) => {
    setExamDates(prev => prev.map((exam) => {
      if (exam.id !== examId) return exam;
      const nextDepartmentIds = exam.departmentIds.filter((id) => id !== departmentId);
      return {
        ...exam,
        departmentIds: nextDepartmentIds,
        allDepartments: false,
      };
    }));
  };

  const handleAllDepartmentsToggle = (examId: string, checked: boolean) => {
    setExamDates(prev => prev.map((exam) => (
      exam.id === examId
        ? {
          ...exam,
          allDepartments: checked,
          departmentIds: checked ? [] : exam.departmentIds,
        }
        : exam
    )));
    if (checked) {
      setPendingDepartmentIds(prev => ({ ...prev, [examId]: null }));
    }
  };

  const handleRemove = (id: string) => {
    setExamDates(prev => prev.filter(e => e.id !== id));
    setPendingDepartmentIds(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleSave = useCallback(async () => {
    if (!hasUpdatePermission) return;
    const invalid = examDates.find((e) => !e.name.trim() || !e.date || (!e.allDepartments && e.departmentIds.length === 0));
    if (invalid) {
      showToast('Lütfen sınav adı, tarih ve departman seçimini tamamlayın', 'warning');
      return;
    }
    setSaving(true);
    try {
      const payload = examDates.map((exam) => ({
        id: exam.id,
        name: exam.name.trim(),
        date: exam.date,
        departmentIds: exam.allDepartments ? [] : expandDepartmentIdsForSave(exam.departmentIds, allDepartments),
      }));
      const res = await ApiService.updateExamDates(payload);
      setExamDates((res.examDates || []).map((exam: any) => {
        const normalized = normalizeExamDate(exam);
        const collapsedDepartmentIds = collapseDepartmentIdsForDisplay(normalized.departmentIds, allDepartments);

        return {
          ...normalized,
          departmentIds: collapsedDepartmentIds,
          allDepartments: collapsedDepartmentIds.length === 0,
        };
      }));
      showToast('Sınav tarihleri başarıyla kaydedildi', 'success');
    } catch (error: any) {
      showToast(error?.message || 'Sınav tarihleri kaydedilirken bir hata oluştu', 'error');
    } finally {
      setSaving(false);
    }
  }, [allDepartments, collapseDepartmentIdsForDisplay, examDates, expandDepartmentIdsForSave, hasUpdatePermission, normalizeExamDate, showToast]);

  useEffect(() => {
    const missingDepartmentIds = [...new Set(
      examDates
        .flatMap((exam) => exam.departmentIds)
        .map((id) => String(id))
        .filter((id) => id && !departmentNames[id])
    )];

    if (missingDepartmentIds.length === 0) {
      return;
    }

    let isMounted = true;

    const resolveMissingDepartmentNames = async () => {
      const entries = await Promise.all(
        missingDepartmentIds.map(async (departmentId) => {
          try {
            const department = await ApiService.getDepartment(departmentId);
            return [departmentId, department?.name || 'Departman'] as const;
          } catch {
            return [departmentId, 'Departman'] as const;
          }
        })
      );

      if (!isMounted) {
        return;
      }

      setDepartmentNames((prev) => ({
        ...prev,
        ...Object.fromEntries(entries),
      }));
    };

    void resolveMissingDepartmentNames();

    return () => {
      isMounted = false;
    };
  }, [departmentNames, examDates]);

  if (loading) return <LoadingState />;

  const accent: SettingsFormAccent = 'fuchsia';
  const a = settingsAccent[accent];

  return (
    <div className={SETTINGS_FORM_PAGE}>
      <div className={SETTINGS_PAGE_INNER}>
        <div className={SETTINGS_SHELL}>
          <div className='mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
            <div>
              <h1 className={SETTINGS_PAGE_TITLE_CLASS}>
                <GraduationCap className='w-6 h-6 text-fuchsia-600' />
                Sınav Tarihleri
              </h1>
              <p className='text-sm text-gray-600 mt-1'>Dashboard&apos;daki geri sayım widget&apos;ı için sınav tarihlerini buradan yönetin.</p>
              <div className={`mt-3 h-1 w-20 rounded-full ${a.underline}`} />
            </div>
            {hasUpdatePermission && (
              <button
                type='button'
                onClick={handleAdd}
                className={`inline-flex shrink-0 items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white shadow-sm ${a.primaryBtn}`}
              >
                <Plus className='w-4 h-4' />
                Sınav Ekle
              </button>
            )}
          </div>

          <div className={SETTINGS_SECTION}>
            <div className='mb-3'>
              <span className={a.badge}>Sınav listesi</span>
            </div>
            <p className='text-sm text-gray-500 mb-4'>Eklenecek sınavların ismi, tarihi ve görünmesini istediğiniz departmanları belirtin. Dashboard widgetında sadece ilgili öğrenciler görür.</p>

          {examDates.length === 0 ? (
            <div className='text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200'>
              <GraduationCap className='w-12 h-12 text-slate-300 mx-auto mb-3' />
              <p className='text-gray-500 font-medium'>Henüz sınav eklenmemiş</p>
              <p className='text-gray-400 text-sm mt-1'>Sınav eklemek için &quot;Sınav Ekle&quot; butonunu kullanın.</p>
            </div>
          ) : (
            <div className='space-y-3'>
              {examDates.map((exam, idx) => (
                <div key={exam.id} className='flex items-start gap-3 p-3 sm:p-4 bg-slate-50/80 rounded-xl border border-slate-200 shadow-sm'>
                  <div className='flex-shrink-0 w-8 h-8 bg-fuchsia-100 rounded-xl flex items-center justify-center'>
                    <span className='text-fuchsia-700 font-bold text-sm'>{idx + 1}</span>
                  </div>
                  <div className='flex-1 space-y-3'>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                      <div>
                        <label className='block text-xs font-medium text-gray-600 mb-1'>Sınav Adı</label>
                        <input
                          type='text'
                          value={exam.name}
                          onChange={(e) => handleChange(exam.id, 'name', e.target.value)}
                          disabled={!hasUpdatePermission}
                          placeholder='Ör: YKS 2025, LGS...'
                          className={`${settingsInputClass(accent)} text-sm`}
                        />
                      </div>
                      <div>
                        <label className='block text-xs font-medium text-gray-600 mb-1'>Sınav Tarihi</label>
                        <input
                          type='date'
                          value={exam.date}
                          onChange={(e) => handleChange(exam.id, 'date', e.target.value)}
                          disabled={!hasUpdatePermission}
                          className={`${settingsInputClass(accent)} text-sm`}
                        />
                      </div>
                    </div>
                    <div>
                      <div className='flex items-center justify-between gap-3 mb-2'>
                        <label className='block text-xs font-medium text-gray-600'>Görünecek Departmanlar</label>
                        <label className='inline-flex items-center gap-2 text-sm text-gray-700'>
                          <input
                            type='checkbox'
                            checked={exam.allDepartments}
                            onChange={(e) => handleAllDepartmentsToggle(exam.id, e.target.checked)}
                            disabled={!hasUpdatePermission}
                            className='rounded border-slate-300 text-fuchsia-600 focus:ring-fuchsia-500'
                          />
                          Tümü
                        </label>
                      </div>

                      {exam.allDepartments ? (
                        <div className='px-3 py-2 text-sm rounded-xl border border-fuchsia-200 bg-fuchsia-50 text-fuchsia-800 font-medium'>
                          Bu sınav tüm departmanlara gösterilecek.
                        </div>
                      ) : (
                        <div className='space-y-3'>
                          {exam.departmentIds.length > 0 && (
                            <div className='flex flex-wrap gap-2'>
                              {exam.departmentIds.map((departmentId) => {
                                return (
                                  <span
                                    key={departmentId}
                                    className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-fuchsia-100 text-fuchsia-800 text-xs font-medium border border-fuchsia-200'
                                  >
                                    {departmentNames[departmentId] || 'Departman'}
                                    {hasUpdatePermission && (
                                      <button
                                        type='button'
                                        onClick={() => handleDepartmentRemove(exam.id, departmentId)}
                                        className='text-fuchsia-600 hover:text-fuchsia-800'
                                      >
                                        ×
                                      </button>
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          )}

                          <DepartmentSelector
                            value={pendingDepartmentIds[exam.id] || null}
                            onChange={(departmentId) => handleDepartmentAdd(exam.id, departmentId)}
                            placeholder='Departman seçip ekleyin'
                            excludeIds={expandDepartmentIdsForSave(exam.departmentIds, allDepartments)}
                            allowClear={false}
                            required={false}
                            inline={true}
                            className='text-sm'
                            disabled={!hasUpdatePermission}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  {hasUpdatePermission && (
                    <button
                      type='button'
                      onClick={() => handleRemove(exam.id)}
                      className='flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 border border-red-200 transition-colors'
                      title='Sınavı Sil'
                    >
                      <Trash2 className='w-4 h-4' />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          </div>

          <div className='sticky bottom-0 z-10 -mx-1 mt-2 rounded-xl border border-slate-200 bg-white/95 p-3 backdrop-blur sm:-mx-0'>
            <div className='flex justify-end'>
              <button
                type='button'
                onClick={handleSave}
                disabled={!hasUpdatePermission || saving}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 ${a.primaryBtn}`}
              >
                {saving ? <Loader2 className='w-4 h-4 animate-spin' /> : <Save className='w-4 h-4' />}
                {saving ? 'Kaydediliyor...' : 'Sınavları Kaydet'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SystemSettingsPage() {
  const navigate = useNavigate();
  const hasListPermission = checkPermission('setting_list');

  useEffect(() => {
    if (!hasListPermission) {
      navigate('/');
    }
  }, [hasListPermission, navigate]);

  const isYesToolsDomain = isYesToolsHostname(window.location.hostname);

  const tabs: TabItemProps[] = useMemo(() => {
    const baseTabs: TabItemProps[] = [
      {
        type: 'custom',
        title: 'Genel Bilgiler',
        path: 'general',
        customComponent: <GeneralSettingsTab />
      },
      {
        type: 'custom',
        title: 'Favicon Dosyaları',
        path: 'favicons',
        customComponent: <FaviconSettingsTab />
      },
      {
        type: 'custom',
        title: 'Eğitim Ayarları',
        path: 'education',
        customComponent: <EducationSettingsTab />
      },
      {
        type: 'custom',
        title: 'Yapay Zeka Ayarları',
        path: 'ai',
        customComponent: <N8nSettingsTab />
      },
      {
        type: 'custom',
        title: 'AI Öğretmenim',
        path: 'ai-teacher',
        customComponent: <AITeacherSettingsTab />
      },
      {
        type: 'custom',
        title: 'Duyuru Ayarları',
        path: 'announcements',
        customComponent: <AnnouncementSettingsTab />
      }
    ];

    if (isYesToolsDomain) {
      baseTabs.splice(1, 0, {
        type: 'custom',
        title: 'Ödeme ve Ücretli Modüller',
        path: 'payments',
        customComponent: <PaymentSettingsTab />
      });

      baseTabs.push({
        type: 'custom',
        title: 'Cafe Ayarları',
        path: 'cafe',
        customComponent: <CafeSettingsTab />
      });

      baseTabs.push({
        type: 'custom',
        title: 'SMS Ayarları',
        path: 'sms',
        customComponent: <SmsSettingsTab />
      });
    };

    baseTabs.push({
      type: 'custom',
      title: 'Kayıt Ayarları',
      path: 'registration',
      customComponent: <RegistrationSettingsTab />
    });

    baseTabs.push({
      type: 'custom',
      title: 'Sınav Tarihleri',
      path: 'exam-dates',
      customComponent: <ExamDatesSettingsTab />
    });

    return baseTabs;
  }, [isYesToolsDomain]);

  if (!hasListPermission) return null;

  return <TabPage title='Sistem Ayarları' tabs={tabs} titleClassName='text-[14px] whitespace-nowrap' tabButtonClassName='text-[12px]' />;
}
